import { useEffect, useState } from "react";
import type {
	EmailGroupingMode,
	Settings,
} from "@/shared/cache/slices/settings.types";
import { Button } from "@/shared/components/ui/button";
import { Card, CardContent } from "@/shared/components/ui/card";
import { Switch } from "@/shared/components/ui/switch";
import { Textarea } from "@/shared/components/ui/textarea";
import {
	type NumericInput,
	SettingGroup,
	SettingRow,
	StepInput,
} from "../SettingsShared";

const PROMPT_FIELD_MAX_CHARS = 2000;

function TrackInternalEmailsToggle({
	trackInternal,
	update,
}: {
	trackInternal: boolean;
	update: (patch: Partial<Settings>) => void;
}) {
	return (
		<SettingRow
			label="Track internal emails"
			description="Track emails where all participants are within your organization"
		>
			<Switch
				checked={trackInternal}
				onCheckedChange={(v) =>
					// Force include=false when track goes off — the dependent
					// narrative-scrubbing setting (managed on the Narratives page)
					// is meaningless without tracking, so we clear it here too to
					// avoid a stale `true` lingering in the cache.
					update(
						v
							? { p_track_internal_emails: true }
							: {
									p_track_internal_emails: false,
									p_include_internal_in_narratives: false,
								},
					)
				}
			/>
		</SettingRow>
	);
}

function EmailGroupingToggles({
	mode,
	update,
}: {
	mode: EmailGroupingMode;
	update: (patch: Partial<Settings>) => void;
}) {
	const groupDrafts =
		mode === "combined" || mode === "grouped" || mode === "individual_review";
	const groupReviews =
		mode === "combined" || mode === "grouped" || mode === "individual_draft";
	const combineAll = mode === "combined";

	const resolveMode = (
		drafts: boolean,
		reviews: boolean,
		combine: boolean,
	): EmailGroupingMode => {
		if (combine) return "combined";
		if (drafts && reviews) return "grouped";
		if (!drafts && reviews) return "individual_draft";
		if (drafts && !reviews) return "individual_review";
		return "individual";
	};

	return (
		<>
			<SettingRow
				label="One entry per thread"
				description="All email activity in a thread — drafting and reviewing — is merged into a single time entry."
			>
				<Switch
					checked={combineAll}
					onCheckedChange={(v) =>
						update({
							email_grouping_mode: v
								? "combined"
								: resolveMode(groupDrafts, groupReviews, false),
						})
					}
				/>
			</SettingRow>
			{!combineAll && (
				<div className="pl-6">
					<SettingRow
						label="One entry per thread for drafts"
						description="Composing multiple emails in the same thread produces one time entry instead of one per email."
					>
						<Switch
							checked={groupDrafts}
							onCheckedChange={(v) =>
								update({
									email_grouping_mode: resolveMode(v, groupReviews, false),
								})
							}
						/>
					</SettingRow>
					<SettingRow
						label="One entry per thread for reviews"
						description="Reading multiple emails in the same thread produces one time entry instead of one per email."
					>
						<Switch
							checked={groupReviews}
							onCheckedChange={(v) =>
								update({
									email_grouping_mode: resolveMode(groupDrafts, v, false),
								})
							}
						/>
					</SettingRow>
				</div>
			)}
		</>
	);
}

function EmailFilteringPromptBlock({
	settings,
	update,
}: {
	settings: Settings;
	update: (patch: Partial<Settings>) => void;
}) {
	const current = settings.p_email_filtering_prompt ?? "";
	const [draft, setDraft] = useState(current);

	useEffect(() => {
		setDraft(current);
	}, [current]);

	const isDirty = draft !== current;
	const overLimit = draft.length > PROMPT_FIELD_MAX_CHARS;

	return (
		<div className="flex flex-col gap-3 py-5 border-b border-border/40">
			<div className="flex flex-col gap-1">
				<p className="text-sm font-medium font-sans text-foreground">
					Email filtering instructions
				</p>
				<p className="text-xs text-muted-foreground leading-relaxed">
					Extra guidance for deciding whether emails without a matched matter
					should be tracked.
				</p>
			</div>
			<Textarea
				value={draft}
				onChange={(e) =>
					setDraft(e.target.value.slice(0, PROMPT_FIELD_MAX_CHARS))
				}
				maxLength={PROMPT_FIELD_MAX_CHARS}
				placeholder='e.g. Ignore emails about conflict checks.'
				className="min-h-[140px]"
			/>
			<div className="flex items-center justify-between">
				<span
					className={`text-xs ${
						overLimit ? "text-destructive" : "text-muted-foreground"
					}`}
				>
					{draft.length} / {PROMPT_FIELD_MAX_CHARS}
				</span>
				{isDirty && (
					<div className="flex gap-2">
						<Button
							variant="outline"
							size="sm"
							onClick={() => setDraft(current)}
						>
							Cancel
						</Button>
						<Button
							size="sm"
							disabled={overLimit}
							onClick={() => update({ p_email_filtering_prompt: draft })}
						>
							Save
						</Button>
					</div>
				)}
			</div>
		</div>
	);
}

export interface TrackingSettingsProps {
	settings: Settings;
	update: (patch: Partial<Settings>) => void;
	emailTime: NumericInput;
	docTime: NumericInput;
	websiteTime: NumericInput;
	inactivityThreshold: NumericInput;
	meetingTime: NumericInput;
}

export function TrackingSettings({
	settings,
	update,
	emailTime,
	docTime,
	websiteTime,
	inactivityThreshold,
	meetingTime,
}: TrackingSettingsProps) {
	return (
		<>
			<SettingGroup label="General">
				<SettingRow
					label="Track entries after export"
					description="Entries will continue to be tracked even after they are exported"
				>
					<Switch
						checked={settings.track_after_exported}
						onCheckedChange={(v) => update({ track_after_exported: v })}
					/>
				</SettingRow>
				<SettingRow
					label="Auto-populate last used matter"
					description="When adding an entry, pre-fill the matter from your most recent entry."
				>
					<Switch
						checked={settings.auto_populate_last_used_matter}
						onCheckedChange={(v) =>
							update({ auto_populate_last_used_matter: v })
						}
					/>
				</SettingRow>
				<SettingRow
					label="Combine actual time"
					description="Bill combined entries from their total tracked time, rounded once. When off, each entry is rounded up to the billing increment before the entries are summed."
				>
					<Switch
						checked={settings.p_combine_actual_units}
						onCheckedChange={(v) => update({ p_combine_actual_units: v })}
					/>
				</SettingRow>
			</SettingGroup>

			<SettingGroup label="Emails">
				<SettingRow
					label="Tracking threshold (seconds)"
					description="How long you must spend on an email before it starts being tracked"
				>
					<StepInput {...emailTime} />
				</SettingRow>
				<TrackInternalEmailsToggle
					trackInternal={settings.p_track_internal_emails}
					update={update}
				/>
				<EmailGroupingToggles
					mode={settings.email_grouping_mode}
					update={update}
				/>
				<EmailFilteringPromptBlock settings={settings} update={update} />
			</SettingGroup>

			<SettingGroup label="Documents">
				<SettingRow
					label="Tracking threshold (seconds)"
					description="How long you must spend on a document before it starts being tracked"
				>
					<StepInput {...docTime} />
				</SettingRow>
			</SettingGroup>

			<SettingGroup label="Browser">
				<SettingRow
					label="Tracking threshold (seconds)"
					description="How long you must spend on a page before it starts being tracked"
				>
					<StepInput {...websiteTime} />
				</SettingRow>
				<SettingRow
					label="Session inactivity timeout (minutes)"
					description="How long you must be inactive before browsing is tracked as a new entry"
				>
					<StepInput {...inactivityThreshold} />
				</SettingRow>
			</SettingGroup>

			<SettingGroup label="Meetings">
				<SettingRow
					label="Time to track meetings (seconds)"
					description="How long you must spend in a meeting before it starts being tracked"
				>
					<StepInput {...meetingTime} />
				</SettingRow>
			</SettingGroup>

			<Card className="mt-8 mb-6 border-destructive/40 bg-destructive/10 dark:bg-destructive/10">
				<CardContent className="p-0">
					<h3 className="border-b border-destructive/20 px-4 py-3 text-base font-medium text-destructive">
						Danger Zone
					</h3>
					<div className="px-4">
						<SettingRow
							label="Automatic tracking"
							description="Turning this off will make it so entries are no longer automatically generated from your activity."
						>
							<Switch
								checked={settings.p_auto_tracking}
								onCheckedChange={(v) => update({ p_auto_tracking: v })}
							/>
						</SettingRow>
					</div>
				</CardContent>
			</Card>
		</>
	);
}
